/**
 * Locates which root timeline contains a deep-link target (`?event=` /
 * `?message=`), so the view can auto-switch to it when the target is not in
 * the active timeline.
 */

import {
  resolveMessageInBranches,
  resolveMessageToEvent,
} from "./resolveMessageToEvent";
import type { Timeline, TimelineEvent, TimelineSpan } from "./timeline/core";

function itemsContainEvent(
  eventId: string,
  items: ReadonlyArray<TimelineEvent | TimelineSpan>
): boolean {
  for (const item of items) {
    if (item.type === "event") {
      // Not every Event union member declares `uuid`; same access pattern as
      // collectReferencedIds in timeline/hooks/useTimelinesArray.ts.
      const uuid = (item.event as { uuid?: string | null }).uuid;
      if (uuid === eventId) return true;
    } else {
      // `?event=` can also target span nodes (agent-card results use the
      // span id as the scroll target), so match span ids too.
      if (item.id === eventId) return true;
      if (itemsContainEvent(eventId, item.content)) return true;
      if (itemsContainEvent(eventId, item.branches)) return true;
    }
  }
  return false;
}

/** True if the timeline's tree (content and branches) contains the target. */
export function timelineContainsEvent(
  eventId: string,
  timeline: Timeline
): boolean {
  return (
    timeline.root.id === eventId ||
    itemsContainEvent(eventId, timeline.root.content) ||
    itemsContainEvent(eventId, timeline.root.branches)
  );
}

/** Index of the first timeline containing the event/span id, or -1. */
export function findTimelineIndexForEvent(
  eventId: string,
  timelines: ReadonlyArray<Timeline>
): number {
  return timelines.findIndex((tl) => timelineContainsEvent(eventId, tl));
}

/** Index of the first timeline whose events resolve the message id, or -1. */
export function findTimelineIndexForMessage(
  messageId: string,
  timelines: ReadonlyArray<Timeline>
): number {
  return timelines.findIndex(
    (tl) =>
      resolveMessageToEvent(messageId, tl.root) !== undefined ||
      resolveMessageInBranches(messageId, tl.root) !== undefined
  );
}
